import React, { useContext, useState } from 'react'
import { EPCRContext } from '../EPCR'
import SimpleTextField from '../../../../components/Forms/SimpleTextField'
//@MUI
import Alert from '@mui/material/Alert'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import NextButton from '../NextButton'
import { Box, Button, Grid, Typography } from '@mui/material'
import BigModal from '../../../../components/Modals/BigModal'
import IVIO from './Modules/IVIO/IVIO'
import moment from 'moment'
import DeleteIcon from '@mui/icons-material/Delete'
import WoundClosure from './Modules/WoundClosure/WoundClosure'
import IVFluids from './Modules/IVFluids/IVFluids'
import AirwayManagement from './Modules/Airway/AirwayManagement'
import Oxygen from './Modules/Oxygen/Oxygen'
import AdministrationSelect from '../../../governance/medsMx/AdministrationSelect'
import { SiteModeContext } from '../../../../components/mainFrame/MainFrame'
import Entonox from './Modules/Entonox/Entonox'
import Splinting from './Modules/Splinting/Splinting'
import CardiacArrest from './Modules/CardiacArrest/CardiacArrest'
import CatHaem from './Modules/CatHaem/CatHaem'
import MedicineSelector from './Modules/MedicineSelector/MedicineSelector'
import Sedation from './Modules/Sedation/Sedation'
import AmendMedicineAdministration from './AmendMedicineAdministration'
import EditIcon from '@mui/icons-material/Edit'
import { Cancel, Check, CheckCircle } from '@mui/icons-material'
import LiveDetailForm from '../liveCapture/LiveDetailForm'
import {
  LiveEntryActions,
  ManualInterventionButton,
} from '../liveCapture/LiveEntryControls'
import { isHiddenEntryKey } from '../liveCapture/liveUi'

const Treatment = () => {
  const { formValues, handleChange, formID } = useContext(EPCRContext)
  const { epcrPracticeMode } = useContext(SiteModeContext)
  const [selectedModule, setSelectedModule] = useState({})
  const [amendMedicine, setAmendMedicine] = useState(null) // { mode, module, index }
  const [detailRequest, setDetailRequest] = useState(null)
  const isCriticalCare = formValues?.mode === 'Critical Care'

  const submitModule = (moduleName, moduleValues) => {
    handleChange({
      moduleName: 'treatment',
      field: 'modules',
      value: [
        ...(formValues?.treatment?.modules ?? []),
        { ...moduleValues, moduleName },
      ],
    })
    setSelectedModule({})
  }

  // TODO: PREVENT DELETING MEDICINE ADMINISTRATION

  const treatmentItems = [
    {
      name: 'Cardiac Arrest',
      module: <CardiacArrest submitModule={submitModule} />,
      color: 'error',
    },
    {
      name: 'Haemorrhage',
      storedName: 'Catastrophic Haemorrhage',
      module: <CatHaem submitModule={submitModule} />,
      color: 'error',
    },
    {
      name: 'Sedation',
      module: <Sedation submitModule={submitModule} />,
      color: 'anPurple',
    },
    {
      name: 'Medicines',
      storedName: 'Medicine',
      module: (
        <MedicineSelector
          submitModule={submitModule}
          epcrID={formID}
          epcrPracticeMode={epcrPracticeMode}
          setSelectedModule={setSelectedModule}
        />
      ),
      color: 'anMainBlue',
      // disableOnClose: true,
    },
    { name: 'IV/IO', module: <IVIO submitModule={submitModule} /> },
    {
      name: 'Wound Closure',
      module: <WoundClosure submitModule={submitModule} />,
    },
    {
      name: 'IV/IO Fluids',
      storedName: 'IV Fluids',
      module: <IVFluids submitModule={submitModule} />,
    },
    {
      name: 'Airway',
      storedName: 'Airway Management',
      module: <AirwayManagement submitModule={submitModule} />,
      color: 'error',
    },
    {
      name: 'Oxygen',
      module: <Oxygen submitModule={submitModule} />,
    },
    {
      name: 'Entonox',
      module: <Entonox submitModule={submitModule} />,
    },
    {
      name: 'Splinting',
      storedName: 'Splinting & Immobilisation',
      module: <Splinting submitModule={submitModule} />,
    },
  ]

  const moduleList = [...(formValues?.treatment?.modules ?? [])].sort(
    (a, b) => moment(b.dateTime).valueOf() - moment(a.dateTime).valueOf(),
  )

  return (
    <Stack gap={2}>
      <EPCRCard>
        <Grid container spacing={1}>
          {treatmentItems.map((x) => (
            <Grid item xs={6} md={4} lg={3} xl={2} key={x.name}>
              <Button
                disabled={!x?.module}
                onClick={() => setSelectedModule(x)}
                variant={
                  moduleList?.some(
                    (m) => m.moduleName === (x.storedName ?? x.name),
                  )
                    ? 'contained'
                    : 'outlined'
                }
                size="small"
                color={x?.color ?? 'primary'}
                fullWidth
              >
                {x.name}
              </Button>
            </Grid>
          ))}
        </Grid>
        {isCriticalCare && (
          <Box mt={1}>
            <ManualInterventionButton
              section="treatment"
              onOpen={setDetailRequest}
              fullWidth
            />
          </Box>
        )}
      </EPCRCard>
      <EPCRCard>
        <SimpleTextField
          value={formValues?.treatment?.treatmentText}
          onChange={(e) =>
            handleChange({
              moduleName: 'treatment',
              field: 'treatmentText',
              value: e.target.value,
            })
          }
          label="Treatment Given"
          multiline
          minRows={2}
        />
      </EPCRCard>

      {moduleList.map((module, index) => {
        return (
          <Grid
            container
            component={Paper}
            padding={1}
            key={module?.entryId ?? `${module?.dateTime}-${index}`}
            rowGap={1}
          >
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <Box display="flex">
                  <Typography variant="title3">{module.moduleName}</Typography>
                </Box>
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  flex={1}
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <LiveEntryActions
                    section="treatment"
                    entry={module}
                    onOpen={setDetailRequest}
                  />
                  <Typography variant="button">
                    {moment(module.dateTime).format('HH:mm')}
                  </Typography>
                  {module.moduleName !== 'Medicine Administration' ? (
                    <DeleteIcon
                      color="error"
                      onClick={() =>
                        handleChange({
                          moduleName: 'treatment',
                          field: 'modules',
                          value: moduleList.filter((x, idx) => index !== idx),
                        })
                      }
                      sx={{ width: '1.5rem', height: '1.5rem' }}
                    />
                  ) : (
                    module.medicineLogId && (
                      <>
                        <EditIcon
                          color="anMainBlue"
                          onClick={() =>
                            setAmendMedicine({ mode: 'edit', module, index })
                          }
                          sx={{
                            width: '1.5rem',
                            height: '1.5rem',
                            cursor: 'pointer',
                          }}
                        />
                        <DeleteIcon
                          color="error"
                          onClick={() =>
                            setAmendMedicine({ mode: 'remove', module, index })
                          }
                          sx={{
                            width: '1.5rem',
                            height: '1.5rem',
                            cursor: 'pointer',
                          }}
                        />
                      </>
                    )
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              display="flex"
              flexWrap="wrap"
              columnGap={5}
              rowGap={1}
            >
              {Object.keys(module)
                .filter(
                  (x) =>
                    !isHiddenEntryKey(x, ['dateTime', 'moduleName', 'Notes']) &&
                    module[x] !== undefined,
                )
                .map((x) => (
                  <Box key={x} display="flex" gap={0.5}>
                    <Typography fontWeight="bold" sx={{ color: '#268EBB' }}>
                      {x}:{' '}
                    </Typography>
                    <Typography variant="body1">
                      {module.isChecklist ? (
                        module[x] ? (
                          <CheckCircle color="success" />
                        ) : (
                          <Cancel color="error" />
                        )
                      ) : x === 'Expiry' ? (
                        moment(module[x]).format('DD/MM/YYYY')
                      ) : Array.isArray(module[x]) ? (
                        module[x].join(', ')
                      ) : (
                        module[x]
                      )}
                    </Typography>
                  </Box>
                ))}
            </Grid>
            {module?.['Notes'] && (
              <Grid item xs={12} lg={6}>
                <Box display="flex" gap={0.5}>
                  <Typography fontWeight="bold">Notes: </Typography>
                  <Typography variant="body1">{module['Notes']}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )
      })}
      <NextButton newModule="Attachments" />
      <LiveDetailForm
        request={detailRequest}
        onClose={() => setDetailRequest(null)}
      />
      <BigModal
        open={Object.keys(selectedModule).length > 0}
        onClose={() => !selectedModule?.disableOnClose && setSelectedModule({})}
      >
        {selectedModule.module}
      </BigModal>
      {amendMedicine && (
        <AmendMedicineAdministration
          mode={amendMedicine.mode}
          module={amendMedicine.module}
          epcrID={formID}
          onClose={() => setAmendMedicine(null)}
          onAmended={(updates) => {
            handleChange({
              moduleName: 'treatment',
              field: 'modules',
              value: moduleList.map((m, idx) =>
                idx === amendMedicine.index ? { ...m, ...updates } : m,
              ),
            })
            setAmendMedicine(null)
          }}
          onRemoved={() => {
            handleChange({
              moduleName: 'treatment',
              field: 'modules',
              value: moduleList.filter(
                (x, idx) => idx !== amendMedicine.index,
              ),
            })
            setAmendMedicine(null)
          }}
        />
      )}
    </Stack>
  )
}

export default Treatment
